/**
 * The header's CoPaya entry point (#1806) plus the screenshot flow.
 *
 * A menu instead of a plain button: "open" mints an entry link straight away,
 * "take screenshot" runs select → capture → annotate → deliver. The component
 * self-gates on the integration status, so the header renders it
 * unconditionally.
 *
 * Capture happens in an effect one paint *after* the selection overlay
 * unmounts (nextPaint), so neither the overlay nor the menu can appear in the
 * rasterized pixels.
 */

import { useEffect, useRef, useState } from 'react';
import { Box, IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Tooltip } from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ScreenshotMonitorIcon from '@mui/icons-material/ScreenshotMonitor';
import { useTranslation } from 'react-i18next';
import { NotepayaIcon } from '@/components/icons/NotepayaIcon';
import { useCopayaStatus, useOpenCopayaWindow } from '@/hooks/useCopaya';
import { useNotify } from '@/hooks/useNotify';
import { captureViewportRegion, nextPaint, type CapturedImage, type SelectionRect } from '@/utils/screenshot';
import { clipboardDelivery } from './copayaDelivery';
import { ScreenshotAnnotationDialog } from './ScreenshotAnnotationDialog';
import { ScreenshotSelectionOverlay } from './ScreenshotSelectionOverlay';

type FlowState =
  | { phase: 'idle' }
  | { phase: 'selecting' }
  | { phase: 'capturing'; rect: SelectionRect }
  | { phase: 'editing'; screenshot: CapturedImage };

export function CopayaMenu() {
  const { t } = useTranslation('copaya');
  const { t: tc } = useTranslation('common');
  const notify = useNotify();
  const { data: copaya } = useCopayaStatus();
  const { openCopaya, isPending } = useOpenCopayaWindow();

  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [flow, setFlow] = useState<FlowState>({ phase: 'idle' });

  // useNotify returns a fresh object per render; going through a ref keeps
  // an unrelated header re-render from cancelling and restarting a capture.
  const reportCaptureFailedRef = useRef(() => {});
  reportCaptureFailedRef.current = () => notify.error(t('captureFailed'), t('captureFailedDesc'));

  useEffect(() => {
    if (flow.phase !== 'capturing') return;
    let cancelled = false;
    void (async () => {
      // The overlay left the React tree when the phase flipped; the double
      // rAF guarantees the browser also painted that frame before we
      // rasterize what is on screen.
      await nextPaint();
      if (cancelled) return;
      try {
        const screenshot = await captureViewportRegion(flow.rect);
        if (!cancelled) setFlow({ phase: 'editing', screenshot });
      } catch {
        if (!cancelled) {
          reportCaptureFailedRef.current();
          setFlow({ phase: 'idle' });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [flow]);

  if (copaya?.enabled !== true) return null;

  const closeMenu = () => setMenuAnchorEl(null);

  /** Runs inside the Send click gesture — the ordering here is load-bearing. */
  const handleSend = ({ pngBlob, summary }: { pngBlob: Promise<Blob>; summary: string }) => {
    // 1. Clipboard first, while this document still has focus — the tab
    //    opened next steals it.
    const delivered = clipboardDelivery.deliver(pngBlob);
    // 2. Claim the tab synchronously (popup blockers) and mint the link. If
    //    the popup is blocked, the same-tab navigation waits for the delivery
    //    so it cannot abort the clipboard write / download mid-flight.
    openCopaya(
      { prefillType: 'bug', ...(summary ? { prefillSummary: summary } : {}) },
      { sameTabNavigateAfter: delivered },
    );
    // 3. Tell the user where the image went once the delivery settles.
    setFlow({ phase: 'idle' });
    delivered
      .then((result) =>
        result.method === 'clipboard'
          ? notify.success(t('copiedTitle'), t('copiedDesc'))
          : notify.success(t('downloadedTitle'), t('downloadedDesc')),
      )
      .catch(() => notify.error(t('captureFailed'), t('captureFailedDesc')));
  };

  return (
    <>
      <Tooltip title={tc('openCopaya')}>
        <span>
          <IconButton
            sx={{ ml: 1 }}
            aria-label={tc('openCopaya')}
            aria-haspopup="true"
            aria-expanded={Boolean(menuAnchorEl)}
            onClick={(e) => setMenuAnchorEl(e.currentTarget)}
            disabled={isPending}
          >
            <NotepayaIcon />
          </IconButton>
        </span>
      </Tooltip>
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={closeMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        // The menu's exit transition keeps its portal mounted briefly after
        // close — a very fast selection could otherwise rasterize it.
        data-screenshot-exclude=""
      >
        <MenuItem
          onClick={() => {
            closeMenu();
            // Land on the submit form, not the community feed (#2259). Someone
            // reaching for this mid-task has something to report; the feed asks
            // them to find their way there first. The type is only a starting
            // point — the form lets them change it.
            openCopaya({ prefillType: 'bug' });
          }}
        >
          <ListItemIcon>
            <OpenInNewIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{tc('openCopaya')}</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            closeMenu();
            setFlow({ phase: 'selecting' });
          }}
        >
          <ListItemIcon>
            <ScreenshotMonitorIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('takeScreenshot')}</ListItemText>
        </MenuItem>
      </Menu>
      {flow.phase === 'selecting' && (
        <ScreenshotSelectionOverlay
          onSelect={(rect) => setFlow({ phase: 'capturing', rect })}
          onCancel={() => setFlow({ phase: 'idle' })}
        />
      )}
      {flow.phase === 'capturing' && (
        // Invisible shield: keeps scrolling, hovers, and clicks from changing
        // the page between the selection and the rasterization. Excluded from
        // the capture itself (and transparent regardless).
        <Box
          data-screenshot-exclude=""
          sx={(theme) => ({
            position: 'fixed',
            inset: 0,
            zIndex: theme.zIndex.tooltip + 1,
            cursor: 'progress',
          })}
        />
      )}
      <ScreenshotAnnotationDialog
        open={flow.phase === 'editing'}
        screenshot={flow.phase === 'editing' ? flow.screenshot : null}
        isSending={isPending}
        onClose={() => setFlow({ phase: 'idle' })}
        onSend={handleSend}
      />
    </>
  );
}
